import React, { useMemo, useState, useRef, useEffect } from 'react';
import style from './bar.graphic.component.module.css';
import { Tooltip } from 'components/Tooltip/tooltip.component';
import { Text } from 'components/text/text.component';

type Item = { date: Date; value: number; color?: string, tooltipLabel?: string };

type Props = {
  data: { date: Date | string; value: number; color?: string, tooltipLabel?: string }[];
  height?: number;
  minBarWidth?: number;
  defaultBarColor?: string;
  groupGap?: number;
}

export const GraphicBarComponent: React.FC<Props> = ({ data, height, minBarWidth = 8, defaultBarColor = '#3b82f6', groupGap = 4 }) => {
//   const [hover, setHover] = useState<{ row: number; index: number } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  const normalized = useMemo(() => data.map(d => ({ ...d, date: typeof d.date === 'string' ? new Date(d.date) : d.date })), [data]);
  const items: Item[] = normalized.map(d => ({ date: d.date as Date, value: d.value, color: d.color, tooltipLabel: d.tooltipLabel }));

  const CHUNK = 48;
  const rows: Item[][] = [];
  for (let i = 0; i < items.length; i += CHUNK) rows.push(items.slice(i, i + CHUNK));
//   const rowsCount = Math.max(1, rows.length);

  const values = normalized.map(d => d.value);
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
//   const rowHeight = height ? Math.max(32, Math.floor(height / rowsCount)) : 80;
  const barGap = 2;

  const groupsPerRow = rows.map(row => {
    const groups: { startIndex: number; endIndex: number; date: Date }[] = [];
    let currentDate: Date | null = null;
    let start = 0;
    for (let i = 0; i < row.length; i++) {
      const d = row[i].date;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}`;
      if (!currentDate) { currentDate = d; start = i; }
      else {
        const k2 = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}-${currentDate.getHours()}`;
        if (k2 !== key) { groups.push({ startIndex: start, endIndex: i - 1, date: currentDate }); currentDate = d; start = i; }
      }
    }
    if (currentDate) groups.push({ startIndex: start, endIndex: row.length - 1, date: currentDate });
    return groups;
  });

  const maxItemsPerRow = Math.max(...rows.map(r => r.length), 1);
  const maxGroupsPerRow = Math.max(...groupsPerRow.map(g => g.length), 1);
  const padding = 0;

  // observe parent width to make component responsive (no horizontal scroll)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setContainerWidth(el.clientWidth);
    });
    observer.observe(el);
    // init
    setContainerWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  const measuredWidth = containerWidth ?? null;
  // compute content area width based on measured container width or fallback
  const contentAreaWidth = measuredWidth ? Math.max(200, measuredWidth - (padding * 2)) : Math.max(200, (padding * 2) + maxItemsPerRow * minBarWidth + (maxItemsPerRow - 1) * barGap + (maxGroupsPerRow - 1) * groupGap - (padding * 2));
  const totalGaps = (((maxItemsPerRow - maxGroupsPerRow) - 1) * barGap) + ((maxGroupsPerRow - 1) * groupGap);
  const barWidthGlobal = (contentAreaWidth - totalGaps) / maxItemsPerRow;
  const barWidth = Math.min(Math.max(minBarWidth, barWidthGlobal), 40);
  console.log(`barWidthGlobal: ${barWidthGlobal}, barWidth: ${barWidth}, contentAreaWidth: ${contentAreaWidth}, measuredWidth: ${measuredWidth}, maxItemsPerRow: ${maxItemsPerRow}, totalGaps: ${totalGaps}`)

  const rowHeight = height ? Math.max(32, height) : 2 * barWidth;

  const scaleY = (v: number) => {
    if (max === min) return rowHeight / 2;
    const n = (v - min) / (max - min);
    return Math.round(rowHeight - n * rowHeight);
  }

  const normalizeColor = (c?: string) => c || defaultBarColor;

//   const onEnter = (_e: React.MouseEvent, rowIndex: number, index: number, _d: Item) => {
//     setHover({ row: rowIndex, index });
//   }
//   const onLeave = () => { setHover(null); }

  return (
    <div className={style.container} ref={containerRef}>
      {/* <div className={style.header}><span className={style.title}>Gráfico de Barras</span></div> */}
  {rows.map((row, rowIndex) => {
          // compute absolute X positions (content area starts at 0)
          const barXs: number[] = [];
          let curX = 0;
          for (let i = 0; i < row.length; i++) {
            barXs.push(curX);
            curX += barWidth;
            if (i < row.length - 1) {
              const isBoundary = groupsPerRow[rowIndex].some(g => g.endIndex === i);
              curX += (isBoundary ? groupGap : barGap);
            }
          }
          const contentWidth = curX;
          // do not center rows — start at left padding
          const startOffset = 0;
          return (
            <div className={style.row} key={`r-${rowIndex}`}> 
              <div className={style.barsArea} style={{ height: rowHeight, width: contentAreaWidth + (padding * 2) }}>
                {/* bars (absolutely positioned inside the row) */}
                {row.map((d, i) => {
                  const valueY = scaleY(d.value);
                  let barH = Math.max(2, rowHeight - valueY);
                  // If the value is exactly 0, ensure the rendered bar is at most 2px high
                  if (d.value === 0) {
                    barH = Math.min(barH, 2);
                  }
                  // cap bar height to at most 4.5x the bar width
                //   const maxBarH = Math.round(barWidth * 4.5);
                //   if (barH > maxBarH) barH = maxBarH;
                  const left = barXs[i] + padding + startOffset; // shift by left padding
                  return (
                    <Tooltip key={`t-${rowIndex}-${i}`} label={d.tooltipLabel !== undefined ? d.tooltipLabel : `${d.date.toLocaleString('pt-BR')} - ${d.value}`}>
                        <div key={`b-${rowIndex}-${i}`} className={style.bar} style={{ position: 'absolute', left, bottom: 0, height: barH, width: barWidth, background: `var(--${normalizeColor(d.color)}-primary-color` }}
                        tabIndex={0}
                        role="listitem"
                        aria-label={d.tooltipLabel !== undefined ? d.tooltipLabel : `${d.date.toLocaleString('pt-BR')} - ${d.value}`}
                        //   onMouseEnter={(e) => onEnter(e, rowIndex, i, d)}
                        //   onMouseLeave={onLeave}
                        //   onFocus={(e) => onEnter(e as any, rowIndex, i, d)}
                        //   onBlur={onLeave}
                        />
                    </Tooltip>
                  )
                })}

                {/* bars area only (separator and labels rendered below) */}

              </div>
              {/* separator */}
                <div className={style.separator} style={{ width: contentWidth, marginLeft: padding + startOffset }}>
                  {groupsPerRow[rowIndex].slice(0, -1).map((g, gi) => {
                    const nextStart = g.endIndex + 1;
                    const prevEnd = barXs[nextStart - 1] + barWidth;
                    const nextStartX = barXs[nextStart];
                    const tickX = Math.round((prevEnd + nextStartX) / 2) - 1;
                    return <div key={`t-${gi}`} className={style.groupTick} style={{ left: tickX }} />
                  })}
                </div>

              {/* grouped labels */}
              <div className={style.groupLabelsRow} style={{ width: contentWidth, height: 0, marginLeft: padding + startOffset }}>
                {groupsPerRow[rowIndex].map((g, gi) => {
                  const startX = barXs[g.startIndex];
                  const endX = barXs[g.endIndex] + barWidth;
                  const center = Math.round((startX + endX) / 2);
                  const label = new Date(g.date).toLocaleString('pt-BR', { hour: '2-digit' });
                  return <Text key={`gl-${gi}`} className={style.groupLabelItem} style={{ left: center }}>{label}</Text>
                })}
              </div>
            </div>
          )
  })}
    {/* tooltip removed */}
    </div>
  )
}
